import React, { useEffect } from 'react';
import { Alert, Button, Center, Stack, Text } from '@mantine/core';
import { CloudOff } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import { useAuth } from '@/contexts/auth/AuthContext';
import { muteServerErrorToasts, SERVER_ERROR_TOAST_IDS } from '@/lib/api/core';

/**
 * Shown when the session could not be verified because the server did not
 * answer. Nothing rejected the session, so this is NOT a sign-out and must never
 * become a redirect to /login (#433). The auth provider keeps re-checking behind
 * it (back online, tab visible, every ~20 s); Try again runs one check now, and
 * Sign in again is the way out.
 *
 * The page already says the server is unreachable, so the "server error" toasts
 * would only repeat it — they are muted while it is up.
 */
export const SessionUnreachable: React.FC = () => {
  const { retrySession, signInAgain, isRechecking } = useAuth();

  useEffect(() => {
    muteServerErrorToasts(true);
    SERVER_ERROR_TOAST_IDS.forEach((id) => sonnerToast.dismiss(id));
    return () => muteServerErrorToasts(false);
  }, []);

  return (
    <Center mih="100vh" p="md">
      <Stack maw={420} w="100%" gap="md">
        <Alert color="yellow" icon={<CloudOff size={16} />} title="Can't reach the server">
          <Text fz="sm">The server is not answering right now. We will keep trying automatically.</Text>
        </Alert>
        <Button onClick={() => { void retrySession(); }} loading={isRechecking} data-testid="session-retry">
          Try again
        </Button>
        <Button variant="subtle" onClick={signInAgain} data-testid="session-sign-in-again">
          Sign in again
        </Button>
      </Stack>
    </Center>
  );
};
